/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

import { IImageBaseViewModel } from 'Scripts/Types/Litium';

import useInView from 'Hooks/useInView';

import VideoThumbnail, {
    type VideoPlayerPlaceholderSrcSet,
} from 'Components/VideoPlayer/VideoPlayerThumbnail';

import PlayIcon from 'Icons/playicon.svg?react';

interface VideoPlayerProps {
    placeholderSmall: IImageBaseViewModel;
    placeholderLarge: IImageBaseViewModel;
    placeholderAlt: string;
    altDescription: string;
    showController?: boolean;
    autoPlay?: boolean;
    muted?: boolean;
    url: string;
    shouldLazyLoad?: boolean;
    videoPlaceholderSizes?: VideoPlayerPlaceholderSrcSet;
}

const VideoPlayer = ({
    placeholderSmall,
    placeholderLarge,
    placeholderAlt,
    altDescription,
    showController = false,
    autoPlay = false,
    muted = true,
    url,
    shouldLazyLoad = false,
    videoPlaceholderSizes = [],
}: VideoPlayerProps) => {
    const hlsInstance = useRef<Hls>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
    const [playRequested, setPlayRequested] = useState<boolean>(autoPlay);
    const [hasStarted, setHasStarted] = useState<boolean>(false);
    const [hasEnded, setHasEnded] = useState<boolean>(false);
    const { inView: isInView } = useInView(videoRef, true);

    useEffect(() => {
        if (!url) return;

        if (Hls.isSupported()) {
            const hls = new Hls({
                maxMaxBufferLength: 60,
            });
            hlsInstance.current = hls;
            hlsInstance.current.loadSource(url);

            hlsInstance.current.currentLevel = -1;
            hlsInstance.current.on(Hls.Events.MANIFEST_LOADED, () => {
                const { levels } = hlsInstance.current;
                if (levels && levels.length > 0) {
                    const lastLevel = levels.length - 1;
                    hlsInstance.current.currentLevel = lastLevel;
                }
            });

            hlsInstance.current.on(Hls.Events.ERROR, () => {
                setHasStarted(false);
            });
        } else if (
            videoRef.current.canPlayType('application/vnd.apple.mpegurl')
        ) {
            videoRef.current.src = url;
            videoRef.current.addEventListener('loadedmetadata', () => {
                setIsVideoReady(true);
            });
        }

        if (videoRef.current) {
            videoRef.current.addEventListener('play', () => {
                setHasStarted(true);
                setHasEnded(false);
            });
            videoRef.current.addEventListener('pause', () => {
                setPlayRequested(false);
            });
            videoRef.current.addEventListener('ended', () => {
                if (!autoPlay) {
                    setHasEnded(true);
                }
            });
        }

        return () => {
            if (hlsInstance.current) {
                if (Hls.isSupported()) {
                    hlsInstance.current.destroy();
                    hlsInstance.current = null;
                } else if (
                    videoRef.current.canPlayType(
                        'application/vnd.apple.mpegurl'
                    )
                ) {
                    videoRef.current.removeEventListener(
                        'loadedmetadata',
                        () => {
                            setIsVideoReady(false);
                        }
                    );
                }
            }

            if (videoRef.current) {
                videoRef.current.removeEventListener('play', () => {});
                videoRef.current.removeEventListener('pause', () => {});
                videoRef.current.removeEventListener('ended', () => {});
            }
        };
    }, [url]);

    useEffect(() => {
        if (Hls.isSupported()) {
            if (
                videoRef.current &&
                hlsInstance.current &&
                !isVideoReady &&
                isInView
            ) {
                hlsInstance.current.attachMedia(videoRef.current);

                hlsInstance.current.on(Hls.Events.MEDIA_ATTACHED, () => {
                    setIsVideoReady(true);
                });
            }
        }

        if (!isVideoReady) return;
        if (!autoPlay) return;

        if (isInView) {
            videoRef.current.play();
        } else {
            videoRef.current.pause();
        }
    }, [isInView]);

    const handlePlayButtonClick = () => {
        if (!videoRef.current) return;

        setPlayRequested(true);

        videoRef.current.play();
    };

    return (
        <div className="relative h-full w-full">
            {((!autoPlay && !playRequested && isVideoReady && !hasStarted) ||
                hasEnded) && (
                <button
                    onClick={handlePlayButtonClick}
                    className="group absolute left-0 top-0 z-[2] h-full w-full"
                >
                    <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full border border-transparent bg-black shadow-[0px_0px_20px_1px_#00000025] transition-colors duration-layout hover:bg-persianPink group-focus-visible:border-black group-focus-visible:shadow-[inset_0_0_0_2px_rgb(27_127_204_/_.9)]">
                        <PlayIcon className="relative left-0.5 h-6 w-6 fill-white text-white" />
                    </span>
                    <span className="sr-only">Play</span>
                </button>
            )}
            <video
                className={`absolute left-0 top-0 z-[1] h-full w-full object-cover transition-opacity delay-200 duration-1000 [&:fullscreen]:!object-contain ${
                    hasStarted && !hasEnded ? 'opacity-100' : 'opacity-0'
                }`}
                playsInline
                ref={videoRef}
                controls={showController || !autoPlay}
                muted={muted}
                loop={autoPlay}
                autoPlay={autoPlay}
                title={altDescription}
            />
            <VideoThumbnail
                hasStarted={hasStarted && !hasEnded}
                videoPlaceholderSizes={videoPlaceholderSizes}
                placeholderLarge={placeholderLarge}
                placeholderSmall={placeholderSmall}
                shouldLazyLoad={shouldLazyLoad}
                placeholderAlt={placeholderAlt}
            />
        </div>
    );
};

export default VideoPlayer;
